import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Grid2 from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectIsLoading } from "../store/slices/session/sessionSelector";
import { LanguageEnum } from "../store/slices/app/appTypes";
import { selectLanguage } from "../store/slices/app/appSelector";
import { setLanguage } from "../store/slices/app/appSlice";
import { loginEmail } from "../store/slices/session/sessionSlice";
import { useNavigate } from "react-router-dom";
import Welcome from "../components/welcome/Welcome";

const LoginPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector(selectIsLoading);
  const language: LanguageEnum = useSelector(selectLanguage);

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  const handleLanguageChange = (event: SelectChangeEvent<LanguageEnum>) => {
    const newLang = event.target.value as LanguageEnum;
    dispatch(setLanguage(newLang));
  };

  const handleEmailLogin = () => {
    if (email && password) {
      dispatch(loginEmail({ email, password }));
    } else {
      alert("Please enter both email and password");
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleEmailLogin();
  };

  return (
    <Grid2 container sx={{ height: "100vh" }}>
      {/* Left Section */}
      <Welcome />

      <Grid2
        component="div"
        size={{ xs: 12, md: 6 }}
        sx={{
          backgroundImage: "none",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
          backgroundColor: theme.palette.background.default,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
          }}
        >
          <Select
            value={language}
            onChange={handleLanguageChange}
            variant="outlined"
            sx={{
              color: "black",
              borderColor: "white",
              "& .MuiSvgIcon-root": {
                color: "black",
              },
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <MenuItem value={LanguageEnum.EN}>{t("languages.en")}</MenuItem>
            <MenuItem value={LanguageEnum.ES}>{t("languages.es")}</MenuItem>
          </Select>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "80%",
            maxWidth: 600,
            padding: "36px",
            backgroundColor: theme.palette.neutral.main,
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="pMedium"
            fontWeight="bold"
            color="primary"
            sx={{ mt: "10px" }}
          >
            {t("login.usernameOrEmail")}
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            sx={{ marginTop: 1 }}
          />

          <Typography variant="pMedium" fontWeight="bold" color="primary">
            {t("login.password")}
          </Typography>

          <TextField
            fullWidth
            type="password"
            variant="outlined"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ marginTop: 1 }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              mt: 1,
            }}
          >
            <Typography
              sx={{
                color: theme.palette.neutral[600],
                fontSize: 14,
                fontFamily: "Nunito",
                fontWeight: 400,
                textDecoration: "underline",
                cursor: "pointer",
                mr: 2,
              }}
              onClick={() => navigate("/forgot-password")}
            >
              {t("login.forgotPassword")}
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            type="submit"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
            endIcon={!isLoading && <ChevronRightIcon />}
          >
            {isLoading ? t("login.logginIn") : t("login.login")}
          </Button>
        </Box>
      </Grid2>
    </Grid2>
  );
};

export default LoginPage;
